import { ThemeComponent } from "@/theme/ThemeComponent";
import { Component, Prop, Ref, SimpleEvent } from "@eastsideco/salvo-ts";
import Player from '@vimeo/player';
import YT from 'youtube-player';
import { YouTubePlayer } from "youtube-player/dist/types";

/**
 * ----------------------------------------------------------------------------
 * Component: Featured Video
 * - Creates featured video element for used with YouTube, Vimeo & Hosted videos.
 * - Video settings will be set within section settings.
 *
 * Section Example
 * {! component 'FeaturedVideo' with section: section !}
 * ------------------------------------------------------------------------------
 */
@Component
export class FeaturedVideo extends ThemeComponent  {
    @Prop videoId!:string;
    @Ref video!: HTMLVideoElement|HTMLIFrameElement|HTMLDivElement;
    @Ref trigger!: HTMLButtonElement;
    @Ref close!: HTMLButtonElement;
    @Ref popup!: HTMLDivElement;

    /**
     * Initialise.
     */
    init() {
        const type = this.video.dataset.type;
        let player:YouTubePlayer|null = null;

        if (type === 'youtube') {
            player = YT(this.video, {
                height: '100%',
                videoId: this.videoId,
                width: '100%',
                playerVars: {
                    playsinline: 1
                }
            });
        }

        this.setEventListeners(type, player);
    }

    /**
     * Set event listeners.
     * @param type - video type (youtube|vimeo|hosted).
     */
    private setEventListeners(type: string|undefined, player:YouTubePlayer|null) {
        this.$listen(this.trigger, 'click', () => {
            this.handlePopup(type, player);

            document.body.addEventListener('keydown', (event) => {
                if (event.key === 'Escape') {
                    this.handleClose(type, player);
                }
            });
        });

        this.$listen(this.close, 'click', () => {
            this.handleClose(type, player);
        });
    }

    /**
     * Handle video popup open.
     * @param type - video type (youtube|vimeo|hosted).
     */
    private handlePopup(type: string|undefined, player:YouTubePlayer|null) {
        this.popup.classList.add('is-active');
        document.body.style.overflow = 'hidden';
        this.playVideo(type, player);
    }

    /**
     * Handle video popup close.
     * @param type - video type (youtube|vimeo|hosted).
     */
    private handleClose(type: string|undefined, player:YouTubePlayer|null) {
        this.popup.classList.remove('is-active');
        document.body.style.overflow = 'auto';
        this.stopVideo(type, player);
    }

    /**
     * Play video.
     * @param type - video type (youtube|vimeo|hosted).
     */
    private playVideo(type:string|undefined, player:YouTubePlayer|null) {
        if (type === 'youtube') {
            this.handleYoutube('play', player);
            return;
        }

        if (type === 'vimeo') {
            this.handleVimeo('play');
            return;
        }

        this.handleHostedVideo('play');
    }

    /**
     * Stop video.
     * @param type - video type (youtube|vimeo|hosted).
     */
    private stopVideo(type:string|undefined, player:YouTubePlayer|null) {
        if (type === 'youtube') {
            this.handleYoutube('stop', player);
            return;
        }

        if (type === 'vimeo') {
            this.handleVimeo('stop');
            return;
        }

        this.handleHostedVideo('stop');
    }

    /**
     * Handle Youtube videos.
     * @param action - play|stop
     */
    private handleYoutube(action:string, player:YouTubePlayer|null) {
        if (player === null) { return; }

        if (action === 'play') {
            player.playVideo().then(() => {
            }).catch((error: Error) => {
                this.$log.error(error.message);
            });
        }

        if (action === 'stop') {
            player.pauseVideo().then(() => {
            }).catch((error: Error) => {
                this.$log.error(error.message);
            });
        }

    }

    /**
     * Handle Vimeo videos.
     * @param action - play|stop
     */
    private handleVimeo(action:string) {
        const player =  new Player(this.video);

        if (action === 'play') {
            player.play().then(() => {
            }).catch((error: Error) => {
                this.$log.error(error.message);
            });
        }

        if (action === 'stop') {
            player.pause().then(() => {
            }).catch((error: Error) => {
                this.$log.error(error.message);
            });
        }
    }

    /**
     * Handle Hosted videos.
     * @param action - play|stop
     */
    private handleHostedVideo(action:string) {
        if (this.video instanceof HTMLVideoElement) {
            if (action === 'play') {
                this.video.play().then(() => {
                }).catch((error: Error) => {
                    this.$log.error(error.message);
                });
            }

            if (action === 'stop') {
                this.video.pause();
            }
        }
    }
}
